import { useEffect, useRef, useState } from 'preact/hooks';
import { useOddball } from '../midi-context';
import type { SynthMode } from '../midi-context';
import { getAudioContext, createReverbBuffer } from '../audio/audio-engine';
import { playSoundfontNote, playSoundfontChord, freqToMidi, isInstrumentLoaded, getCurrentProgram } from '../audio/soundfont-player';
import type { CaughtEvent, LandedEvent, CCUpdateEvent, ThrowStartEvent, IdleEvent } from '../../src/types';
import { OddballCC } from '../../src/types';

const SCALES: Record<string, number[]> = {
  'Cmaj7':    [261.63, 329.63, 392.00, 493.88, 523.25, 659.25, 783.99, 987.77],
  'Dm7':     [293.66, 349.23, 440.00, 523.25, 587.33, 698.46, 880.00, 1046.50],
  'Em pent': [164.81, 196.00, 246.94, 293.66, 329.63, 392.00, 493.88, 587.33],
  'A blues': [220.00, 261.63, 293.66, 311.13, 329.63, 392.00, 440.00, 523.25],
  'F Lydian':[174.61, 246.94, 277.18, 329.63, 349.23, 440.00, 493.88, 659.25],
};

// Bright major resolve chords for triumphant endings (root, 3rd, 5th in Hz)
const RESOLVE_CHORDS: Record<string, number[]> = {
  'Cmaj7':    [261.63, 329.63, 392.00],  // C E G
  'Dm7':      [293.66, 369.99, 440.00],  // D F# A  (Picardy third — major resolve)
  'Em pent':  [329.63, 415.30, 493.88],  // E G# B
  'A blues':  [220.00, 277.18, 329.63],  // A C# E
  'F Lydian': [349.23, 440.00, 523.25],  // F A C
};

// Spin axis → scale mapping (dominant orient axis while airborne)
const AXIS_SCALES: Record<string, string> = {
  x: 'A blues',
  y: 'Em pent',
  z: 'F Lydian',
  none: 'Cmaj7',
};

const SCALE_NAMES = Object.keys(SCALES);

type FlightState = 'ready' | 'airborne' | 'caught';
type ArpDirection = 'up' | 'down';

interface ArpEngine {
  ctx: AudioContext;
  masterGain: GainNode;
  reverb: ConvolverNode;
  wetGain: GainNode;
  arpInterval: number;
  arpIndex: number;
  arpSpeed: number;
  patternSeq: number[];
  patternPos: number;
  lastNoteIdx: number;
  recentNotes: number[];
  octaveShift: number;
  speedMul: number;
}

function createArpEngine(): ArpEngine {
  const ctx = getAudioContext();
  const masterGain = ctx.createGain();
  masterGain.gain.value = 0.4;

  const reverb = ctx.createConvolver();
  reverb.buffer = createReverbBuffer(ctx, 2, 1.8);
  const wetGain = ctx.createGain();
  wetGain.gain.value = 0.3;

  const dryGain = ctx.createGain();
  dryGain.gain.value = 0.7;

  masterGain.connect(dryGain).connect(ctx.destination);
  masterGain.connect(reverb).connect(wetGain).connect(ctx.destination);

  return { ctx, masterGain, reverb, wetGain, arpInterval: 0, arpIndex: 0, arpSpeed: 160, patternSeq: [], patternPos: 0, lastNoteIdx: 0, recentNotes: [], octaveShift: 0, speedMul: 1 };
}

function buildPatternSeq(len: number, direction: ArpDirection): number[] {
  // Ping-pong: bounce at endpoints to avoid jarring jumps
  // up:   [0,1,2,3,4,5,6,7,6,5,4,3,2,1] then repeats
  // down: [7,6,5,4,3,2,1,0,1,2,3,4,5,6] then repeats
  if (len <= 2) return Array.from({ length: len }, (_, i) => direction === 'up' ? i : len - 1 - i);
  const asc = Array.from({ length: len }, (_, i) => i);
  const descMid = Array.from({ length: len - 2 }, (_, i) => len - 2 - i);
  if (direction === 'up') return [...asc, ...descMid];
  const desc = Array.from({ length: len }, (_, i) => len - 1 - i);
  const ascMid = Array.from({ length: len - 2 }, (_, i) => i + 1);
  return [...desc, ...ascMid];
}

function buildWanderSeq(len: number, count: number): number[] {
  const seq: number[] = [];
  let pos = Math.floor(Math.random() * len);
  const steps = [-2, -1, -1, 0, 1, 1, 2];
  for (let i = 0; i < count; i++) {
    seq.push(pos);
    const step = steps[Math.floor(Math.random() * steps.length)];
    pos = Math.max(0, Math.min(len - 1, pos + step));
  }
  return seq;
}

function playArpNote(eng: ArpEngine, brightness: number, scale: number[], synthMode: SynthMode) {
  const { ctx, masterGain } = eng;
  const t = ctx.currentTime;

  const noteIdx = eng.patternSeq[eng.patternPos % eng.patternSeq.length];
  eng.patternPos++;
  eng.lastNoteIdx = noteIdx;
  eng.recentNotes.push(noteIdx);
  if (eng.recentNotes.length > 4) eng.recentNotes.shift();
  const freq = scale[noteIdx] * Math.pow(2, eng.octaveShift);
  eng.arpIndex++;

  const vol = 0.15 + brightness * 0.2;

  if (synthMode === 'soundfont' && isInstrumentLoaded(getCurrentProgram())) {
    playSoundfontNote(masterGain, freqToMidi(freq), 0.25, vol);
  } else {
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = freq;

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 1000 + brightness * 4000;

    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(vol, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.25);

    osc.connect(filter).connect(g).connect(masterGain);
    osc.start(t);
    osc.stop(t + 0.3);
  }

  return noteIdx;
}

/** Get a scale frequency, wrapping into higher/lower octaves as needed */
function scaleFreq(scale: number[], idx: number): number {
  const len = scale.length;
  const octave = Math.floor(idx / len);
  const wrapped = ((idx % len) + len) % len;
  return scale[wrapped] * Math.pow(2, octave);
}

function playResolve(eng: ArpEngine, velocity: number, scale: number[], scaleName: string, synthMode: SynthMode) {
  const { ctx, masterGain } = eng;
  const t = ctx.currentTime;
  const vol = (velocity / 127) * 0.32;

  // Get the last melody note frequency for voice leading
  const lastIdx = eng.recentNotes.length > 0 ? eng.recentNotes[eng.recentNotes.length - 1] : eng.lastNoteIdx;
  const lastFreq = scaleFreq(scale, lastIdx) * Math.pow(2, eng.octaveShift);

  // Always resolve to a bright major chord — bestShift handles octave placement
  const baseChord = RESOLVE_CHORDS[scaleName] || RESOLVE_CHORDS['Cmaj7'];

  // Find the octave shift that places the chord closest to where the melody was
  const midBase = baseChord[1];
  let bestShift = 0;
  let bestDist = Infinity;
  for (let shift = -2; shift <= 3; shift++) {
    const shifted = midBase * Math.pow(2, shift);
    const dist = Math.abs(Math.log2(shifted / lastFreq));
    if (dist < bestDist) {
      bestDist = dist;
      bestShift = shift;
    }
  }

  // Voicing: bass (root down an octave) + major triad
  const chordFreqs = baseChord.map(f => f * Math.pow(2, bestShift));
  const bassFreq = chordFreqs[0] / 2;
  const voicing = [bassFreq, ...chordFreqs];

  if (synthMode === 'soundfont' && isInstrumentLoaded(getCurrentProgram())) {
    playSoundfontChord(masterGain, voicing.map(freqToMidi), 1.2, vol);
  } else {
    voicing.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      osc.type = i === 0 ? 'triangle' : 'sine';
      osc.frequency.value = freq;
      const g = ctx.createGain();
      const v = vol * (i === 0 ? 0.6 : 1 - i * 0.06);
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(v, t + 0.015);
      g.gain.setValueAtTime(v * 0.8, t + 0.35);
      g.gain.exponentialRampToValueAtTime(0.001, t + 1.2);
      osc.connect(g).connect(masterGain);
      osc.start(t);
      osc.stop(t + 1.3);
    });
  }
}


export function ThrowArp() {
  const { parser, detector, throwDetector, connected, speedModifier, octaveOffset, synthMode } = useOddball();
  const engineRef = useRef<ArpEngine | null>(null);
  const synthModeRef = useRef<SynthMode>('oscillator');
  const [state, setState] = useState<FlightState>('ready');
  const [throwCount, setThrowCount] = useState(0);
  const [catchCount, setCatchCount] = useState(0);
  const [notes, setNotes] = useState<number[]>([]);
  const [cycleScales, setCycleScales] = useState(true);
  const [alternate, setAlternate] = useState(true);
  const [wander, setWander] = useState(false);
  const [spinScale, setSpinScale] = useState(false);
  const [direction, setDirection] = useState<ArpDirection>('up');
  const [scaleName, setScaleName] = useState(SCALE_NAMES[0]);
  const [arpSpeed, setArpSpeed] = useState(160);
  const stateTimeoutRef = useRef(0);
  const currentScaleRef = useRef(SCALES[SCALE_NAMES[0]]);
  const scaleNameRef = useRef(SCALE_NAMES[0]);
  const speedRef = useRef(1.0);
  const octaveRef = useRef(0);
  const throwCountRef = useRef(0);
  const cycleEnabledRef = useRef(true);
  const alternateRef = useRef(true);
  const wanderRef = useRef(false);
  const spinScaleRef = useRef(false);
  const directionRef = useRef<ArpDirection>('up');
  const throwsSinceFlipRef = useRef(0);
  const airborneRef = useRef(false);
  const airborneStartRef = useRef(0);
  const catchBackoffUntilRef = useRef(0);
  const [spinAxis, setSpinAxis] = useState('');

  // Orient axis delta tracking for spin axis detection
  const orientPrevRef = useRef({ x: 64, y: 64, z: 64 });
  const orientDeltaRef = useRef({ x: 0, y: 0, z: 0 });

  useEffect(() => { cycleEnabledRef.current = cycleScales; }, [cycleScales]);
  useEffect(() => { alternateRef.current = alternate; }, [alternate]);
  useEffect(() => { wanderRef.current = wander; }, [wander]);
  useEffect(() => { spinScaleRef.current = spinScale; }, [spinScale]);
  useEffect(() => { speedRef.current = speedModifier; }, [speedModifier]);
  useEffect(() => { octaveRef.current = octaveOffset; }, [octaveOffset]);
  useEffect(() => { synthModeRef.current = synthMode; }, [synthMode]);
  useEffect(() => {
    if (!connected) {
      const eng = engineRef.current;
      if (eng) { clearTimeout(eng.arpInterval); eng.arpInterval = 0; }
      airborneRef.current = false;
      setState('ready');
    }
  }, [connected]);

  useEffect(() => {
    const eng = createArpEngine();
    engineRef.current = eng;

    const onThrowStart = (_evt: ThrowStartEvent) => {
      if (Date.now() < catchBackoffUntilRef.current) return;
      setState('airborne');
      airborneRef.current = true;
      airborneStartRef.current = Date.now();
      orientDeltaRef.current = { x: 0, y: 0, z: 0 };
      setSpinAxis('');
      throwCountRef.current++;
      setThrowCount(throwCountRef.current);
      eng.arpIndex = 0;
      eng.arpSpeed = 145;
      eng.recentNotes = [];
      setNotes([]);

      // Update scale if cycling
      if (cycleEnabledRef.current) {
        const idx = Math.floor((throwCountRef.current - 1) / 2) % SCALE_NAMES.length;
        currentScaleRef.current = SCALES[SCALE_NAMES[idx]];
        scaleNameRef.current = SCALE_NAMES[idx];
        setScaleName(SCALE_NAMES[idx]);
      } else {
        currentScaleRef.current = SCALES[SCALE_NAMES[0]];
        scaleNameRef.current = SCALE_NAMES[0];
        setScaleName(SCALE_NAMES[0]);
      }

      const scale = currentScaleRef.current;

      // Determine direction: alternate flips every 3 throws, otherwise always up
      if (alternateRef.current) {
        throwsSinceFlipRef.current++;
        if (throwsSinceFlipRef.current >= 3) {
          throwsSinceFlipRef.current = 0;
          directionRef.current = directionRef.current === 'up' ? 'down' : 'up';
        }
      } else {
        directionRef.current = 'up';
        throwsSinceFlipRef.current = 0;
      }
      setDirection(directionRef.current);

      if (wanderRef.current) {
        eng.patternSeq = buildWanderSeq(scale.length, 64);
      } else {
        eng.patternSeq = buildPatternSeq(scale.length, directionRef.current);
      }
      eng.patternPos = 0;

      // Start arpeggio
      const step = () => {
        const noteIdx = playArpNote(eng, 0.5, scale, synthModeRef.current);
        setNotes((prev) => [...prev, noteIdx]);
        eng.arpInterval = window.setTimeout(step, eng.arpSpeed / eng.speedMul);
      };
      eng.arpInterval = window.setTimeout(step, 50);
    };

    const onCaughtOrLanded = (evt: CaughtEvent | LandedEvent) => {
      const velocity = evt.type === 'caught' ? (evt as CaughtEvent).catchVelocity : 80;
      airborneRef.current = false;
      clearTimeout(eng.arpInterval);
      eng.arpInterval = 0;
      playResolve(eng, velocity, currentScaleRef.current, scaleNameRef.current, synthModeRef.current);
      catchBackoffUntilRef.current = Date.now() + 200;
      setState('caught');
      setCatchCount((n) => n + 1);
      clearTimeout(stateTimeoutRef.current);
      stateTimeoutRef.current = window.setTimeout(() => setState('ready'), 2000);
    };

    const onIdle = (_evt: IdleEvent) => {
      airborneRef.current = false;
      clearTimeout(eng.arpInterval);
      eng.arpInterval = 0;
      setState('ready');
    };

    // Read spin speed + orient axis from raw CC while airborne
    const onCC = (evt: CCUpdateEvent) => {
      eng.octaveShift = octaveRef.current;
      eng.speedMul = speedRef.current;
      if (!airborneRef.current) return;
      if (evt.cc === OddballCC.SpinSmoothed) {
        if (evt.value > 10) {
          eng.arpSpeed = 48 + (1 - evt.value / 127) * 97;
          setArpSpeed(Math.round(eng.arpSpeed));
        }
      }
      // Track orient axis deltas for spin axis detection
      if (evt.cc === OddballCC.OrientX || evt.cc === OddballCC.OrientY || evt.cc === OddballCC.OrientZ) {
        const axis = evt.cc === OddballCC.OrientX ? 'x' : evt.cc === OddballCC.OrientY ? 'y' : 'z';
        const prev = orientPrevRef.current[axis];
        orientDeltaRef.current[axis] += Math.abs(evt.value - prev);
        orientPrevRef.current[axis] = evt.value;

        // Determine dominant axis and update scale if spin-axis mode is on
        if (spinScaleRef.current) {
          const d = orientDeltaRef.current;
          const total = d.x + d.y + d.z;
          let dominant = 'none';
          if (total > 20) { // need some movement to judge
            if (d.x >= d.y && d.x >= d.z) dominant = 'x';
            else if (d.y >= d.z) dominant = 'y';
            else dominant = 'z';
          }
          const newName = AXIS_SCALES[dominant];
          setSpinAxis(dominant === 'none' ? '' : dominant.toUpperCase());
          currentScaleRef.current = SCALES[newName];
          scaleNameRef.current = newName;
          setScaleName(newName);
        }
      }
      if (evt.cc === OddballCC.FreeFall && airborneRef.current) {
        const elapsed = Date.now() - airborneStartRef.current;
        if (evt.value < 10) {
          eng.arpSpeed = Math.max(eng.arpSpeed, 120 + elapsed * 0.1);
          setArpSpeed(Math.round(eng.arpSpeed));
        }
      }
    };

    throwDetector.on('throwStart', onThrowStart);
    throwDetector.on('caught', onCaughtOrLanded);
    throwDetector.on('landed', onCaughtOrLanded);
    detector.on('idle', onIdle);
    parser.on('cc', onCC);

    return () => {
      throwDetector.off('throwStart', onThrowStart);
      throwDetector.off('caught', onCaughtOrLanded);
      throwDetector.off('landed', onCaughtOrLanded);
      detector.off('idle', onIdle);
      parser.off('cc', onCC);
      clearTimeout(eng.arpInterval);
      clearTimeout(stateTimeoutRef.current);
      eng.masterGain.disconnect();
    };
  }, [parser, detector, throwDetector]);

  const stateColors: Record<FlightState, string> = {
    ready: '#555',
    airborne: '#a74',
    caught: '#4a7',
  };

  const stateLabels: Record<FlightState, string> = {
    ready: 'Ready',
    airborne: 'Airborne!',
    caught: 'Caught!',
  };

  const scaleLen = currentScaleRef.current.length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '24px', paddingTop: '40px' }}>
      <h2 style={{ fontSize: '1rem', fontWeight: 300, color: '#888' }}>Throw Arpeggio</h2>

      <div
        style={{
          fontSize: '2rem',
          fontWeight: 300,
          color: stateColors[state],
          letterSpacing: '0.1em',
          transition: 'color 0.2s',
        }}
      >
        {stateLabels[state]}
      </div>

      {/* Note visualization */}
      <div style={{ display: 'flex', gap: '4px', height: '40px', alignItems: 'flex-end' }}>
        {notes.slice(-16).map((idx, i) => (
          <div
            key={i}
            style={{
              width: '6px',
              height: `${10 + (idx / scaleLen) * 30}px`,
              background: stateColors[state],
              borderRadius: '2px',
              opacity: 0.4 + (i / 16) * 0.6,
            }}
          />
        ))}
      </div>

      {/* Counters + scale name + direction */}
      <div style={{ display: 'flex', gap: '24px', fontSize: '0.8rem', color: '#666' }}>
        <span>Throws: {throwCount}</span>
        <span>Catches: {catchCount}</span>
        <span style={{ color: cycleScales ? '#a74' : '#666' }}>Scale: {scaleName}</span>
        {alternate && !wander && (
          <span style={{ color: '#a74' }}>{direction === 'up' ? '\u2191 Up' : '\u2193 Down'}</span>
        )}
        {wander && (
          <span style={{ color: '#a74' }}>~ Wander</span>
        )}
        {spinScale && spinAxis && (
          <span style={{ color: '#a74' }}>Axis: {spinAxis}</span>
        )}
      </div>

      {/* Spin hint — prominent */}
      <div style={{
        padding: '8px 16px',
        borderRadius: 8,
        background: state === 'airborne' ? '#2a1a0a' : '#1a1a1a',
        border: `1px solid ${state === 'airborne' ? '#a74' : '#333'}`,
        transition: 'all 0.3s',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: '0.95rem', color: state === 'airborne' ? '#fa6' : '#888', fontWeight: 500 }}>
          Spin while airborne = faster arps
        </div>
        {state === 'airborne' && (
          <div style={{ fontSize: '0.75rem', color: '#a74', fontFamily: 'monospace', marginTop: 4 }}>
            arp speed: {arpSpeed}ms
          </div>
        )}
      </div>

      {/* Checkboxes */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.8rem', color: '#888', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={cycleScales}
            onChange={(e) => setCycleScales((e.target as HTMLInputElement).checked)}
            style={{ accentColor: '#a74' }}
          />
          Cycle scales every 2 throws
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.8rem', color: '#888', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={alternate}
            onChange={(e) => setAlternate((e.target as HTMLInputElement).checked)}
            style={{ accentColor: '#a74' }}
          />
          Alternate (flip direction every 3 throws)
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.8rem', color: '#888', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={wander}
            onChange={(e) => setWander((e.target as HTMLInputElement).checked)}
            style={{ accentColor: '#a74' }}
          />
          Wander (semi-random walk)
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.8rem', color: '#888', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={spinScale}
            onChange={(e) => setSpinScale((e.target as HTMLInputElement).checked)}
            style={{ accentColor: '#a74' }}
          />
          Spin axis selects scale
        </label>
      </div>

      <div style={{ fontSize: '0.7rem', color: '#444', maxWidth: '300px', textAlign: 'center', lineHeight: 1.6 }}>
        Throw = ascending arpeggio &middot; Catch = resolving chord
      </div>
    </div>
  );
}
